import { api } from './authService';
import { xProjectIdConfig } from '../utils/projectScope';

function getTenantCode(): string {
  return localStorage.getItem('currentTenant') || 'default';
}

export interface UserPermissionRecord {
  id?: number;
  tenant_code: string;
  project_id?: string | number | null;
  user_name: string;
  permission_name: string;
  granted: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface UserPermissionSummary {
  userName: string;
  permissions: Array<{ permissionName: string; granted: boolean }>;
  grantedCount: number;
  total: number;
}

export async function listUserPermissions(projectId?: string | number | null): Promise<UserPermissionSummary[]> {
  const tenant = getTenantCode();
  const response = await api.get<{ success: boolean; data: UserPermissionSummary[] }>(
    `/${tenant}/user-permissions`,
    xProjectIdConfig(projectId)
  );
  return response.data.data;
}

export async function getUserPermissions(userName: string, projectId?: string | number | null): Promise<UserPermissionRecord[]> {
  const tenant = getTenantCode();
  const response = await api.get<{ success: boolean; data: UserPermissionRecord[] }>(
    `/${tenant}/user-permissions/${encodeURIComponent(userName)}`,
    xProjectIdConfig(projectId)
  );
  return response.data.data;
}

export async function upsertUserPermission(
  payload: { userName: string; permissionName: string; granted: boolean },
  projectId?: string | number | null
): Promise<void> {
  const tenant = getTenantCode();
  await api.post(
    `/${tenant}/user-permissions`,
    payload,
    xProjectIdConfig(projectId)
  );
}

export async function bulkUpsertUserPermissions(
  userName: string,
  permissions: Array<{ permissionName: string; granted: boolean }>,
  projectId?: string | number | null
): Promise<void> {
  const tenant = getTenantCode();
  await api.post(
    `/${tenant}/user-permissions/${encodeURIComponent(userName)}/bulk`,
    { permissions },
    xProjectIdConfig(projectId)
  );
}

export async function deleteUserPermission(
  userName: string,
  permissionName: string,
  projectId?: string | number | null
): Promise<void> {
  const tenant = getTenantCode();
  await api.delete(
    `/${tenant}/user-permissions/${encodeURIComponent(userName)}/${encodeURIComponent(permissionName)}`,
    xProjectIdConfig(projectId)
  );
}

export async function deleteAllPermissionsForUser(userName: string, projectId?: string | number | null): Promise<void> {
  const tenant = getTenantCode();
  await api.delete(
    `/${tenant}/user-permissions/${encodeURIComponent(userName)}`,
    xProjectIdConfig(projectId)
  );
}

export async function restoreUserPermissionsFromRemote(projectId?: string | number | null): Promise<{
  success: boolean;
  message?: string;
  data?: {
    totalUsers: number;
    totalPermissions: number;
    remoteSummary?: any;
  };
}> {
  const tenant = getTenantCode();
  const response = await api.post(
    `/${tenant}/user-permissions/restore-remote`,
    undefined,
    xProjectIdConfig(projectId)
  );
  return response.data;
}

export async function syncUserPermissionsRemote(projectId?: string | number | null): Promise<{
  success: boolean;
  message?: string;
  data?: any;
}> {
  const tenant = getTenantCode();
  const response = await api.post(
    `/${tenant}/user-permissions/sync-remote`,
    undefined,
    xProjectIdConfig(projectId)
  );
  return response.data;
}
